// src/components/services/BusinessLoans.jsx
import React from "react";
import { motion } from "framer-motion";
import { Link } from 'react-router-dom'; // Import Link
import {
    MdOutlineBusinessCenter,
    MdOutlineAttachMoney,
    MdOutlineDescription,
    MdOutlineVerifiedUser,
    MdOutlineSecurity,
} from "react-icons/md";
import {
    FaPhoneAlt,
    FaHandshake,
    FaChartLine,
    FaBuilding,
    FaCalculator,
} from "react-icons/fa";


function BusinessLoans() {
    const serviceName = "Business Loans"; // Set service name

    // Animation Variants (Keep, or move to a separate file)
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2, // Stagger animation of child elements
            },
        },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0 },
    };
    return (
        <section className="py-16 md:py-24 px-4 md:px-8 bg-gray-50 text-gray-700">
            <div className="container mx-auto">
                <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={containerVariants}
                >
                    <motion.h1
                        variants={itemVariants}
                        className="text-4xl md:text-5xl font-bold text-gray-800 mb-6 md:mb-8 text-center"
                    >
                        Business Loans
                    </motion.h1>
                    <motion.p
                        variants={itemVariants}
                        className="text-lg md:text-xl text-gray-600 mb-10 md:mb-12 text-center max-w-3xl mx-auto"
                    >
                        Fuel your business growth with a Business Loan from Keziah Global Private
                        Limited, in partnership with Fino Payments Bank. We offer flexible
                        financing solutions to help you expand, invest in new equipment,
                        manage working capital, or seize new opportunities.
                    </motion.p>

                    {/* Features and Benefits */}
                    <motion.div
                        variants={itemVariants}
                        className="mb-12 md:mb-16 bg-white rounded-xl shadow-md p-6 md:p-10"
                    >
                        <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 text-center">
                            Key Features
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                            {/* Feature 1 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <FaChartLine className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        Loans for Business Expansion
                                    </h4>
                                    <p className="text-gray-600">
                                        Get the funds you need to expand your operations, open new
                                        locations, or increase your market reach.
                                    </p>
                                </div>
                            </motion.div>
                            {/* Feature 2 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2, delay: 0.1 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <MdOutlineAttachMoney className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        Flexible Loan Amounts
                                    </h4>
                                    <p className="text-gray-600">
                                        Borrow amounts tailored to your specific business needs and
                                        financial capacity.
                                    </p>
                                </div>
                            </motion.div>
                            {/* Feature 3 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2, delay: 0.2 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <FaBuilding className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        Working Capital Loans
                                    </h4>
                                    <p className="text-gray-600">
                                        Manage your day-to-day expenses and ensure smooth operations
                                        with our working capital solutions.
                                    </p>
                                </div>
                            </motion.div>
                            {/* Feature 4 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2, delay: 0.3 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <MdOutlineDescription className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        Simple Documentation
                                    </h4>
                                    <p className="text-gray-600">
                                        Hassle-free application process with minimal paperwork.
                                    </p>
                                </div>
                            </motion.div>
                            {/* Feature 5 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2, delay: 0.4 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <MdOutlineVerifiedUser className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        Quick Processing and Disbursal
                                    </h4>
                                    <p className="text-gray-600">
                                        Get your loan processed and disbursed quickly to meet your
                                        urgent business requirements.
                                    </p>
                                </div>
                            </motion.div>
                            {/* Feature 6 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2, delay: 0.5 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <MdOutlineSecurity className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                       Transparent and Ethical Practices
                                    </h4>
                                    <p className="text-gray-600">
                                        We adhere to fair and transparent lending practices with no
                                        hidden charges.
                                    </p>
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>

                    {/* Eligibility and Documents Required */}
                    <motion.div
                        variants={itemVariants}
                        className="mb-12 md:mb-16 bg-white rounded-xl shadow-md p-6 md:p-10"
                    >
                        <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 text-center">
                            Eligibility and Documents Required
                        </h3>
                        <p className="text-gray-600 text-lg mb-4">
                            To be eligible for a Business Loan, you typically need to meet the
                            following criteria:
                        </p>
                        <ul className="list-disc pl-8 space-y-4 text-gray-600 text-lg mb-6">
                            <li>
                                Your business should be operational for a minimum period (e.g.,
                                1-3 years).
                            </li>
                            <li>
                                Meet the minimum annual turnover requirement (as per lender's
                                policy).
                            </li>
                            <li>
                                Have a good credit history (depending on the lender's policies).
                            </li>
                        </ul>
                        <p className="text-gray-600 text-lg mb-4">
                            Documents usually required include:
                        </p>
                        <ul className="list-disc pl-8 space-y-4 text-gray-600 text-lg">
                            <li>KYC documents of the business and its owners/partners</li>
                            <li>Business registration proof</li>
                            <li>Bank statements (e.g., last 6-12 months)</li>
                            <li>
                                Financial statements (e.g., audited financials, ITR for the past
                                2-3 years)
                            </li>
                            <li>Business plan or project report (for larger loans)</li>
                        </ul>
                    </motion.div>



                    {/* Call to Action */}
                    <motion.div
                        variants={itemVariants}
                        className="mb-12 md:mb-16 bg-white rounded-xl shadow-md p-6 md:p-10"
                    >
                        <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 text-center">
                            Apply for a Business Loan Today!
                        </h3>
                        <p className="text-gray-600 text-lg text-center">
                            Visit our branch in Salem, Tamil Nadu, to apply for a Business Loan
                            and take your business to new heights. Our team is ready to assist
                            you with the application process. You can also request a callback
                            to learn more.
                        </p>
                        <motion.div
                            variants={itemVariants}
                            className="mt-10 flex flex-col md:flex-row justify-center space-y-4 md:space-y-0 md:space-x-6"
                        >
                            <a
                                href="tel:9043588446"
                                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-full text-base md:text-lg transition duration-300 ease-in-out shadow-md hover:shadow-lg text-center w-full md:w-auto flex items-center justify-center"
                                whileHover={{ scale: 1.05 }}
                            >
                                <FaPhoneAlt className="inline-block mr-2" />
                                Call Now
                            </a>
                            {/* Use Link for the callback button */}
                            <Link to={`/callback?serviceName=${encodeURIComponent(serviceName)}`}>
                                <motion.button
                                    className="bg-gray-800 hover:bg-gray-700 text-white font-bold py-3 px-6 rounded-full text-base md:text-lg transition duration-300 ease-in-out shadow-md hover:shadow-lg text-center w-full md:w-auto flex items-center justify-center"
                                    whileHover={{ scale: 1.05 }}
                                >
                                    Request a Callback
                                </motion.button>
                            </Link>
                        </motion.div>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
}

export default BusinessLoans;